<template>
  <el-container>
    <router-view v-if="isRouterAlive" v-slot="{ Component, route }">
      <template v-if="Component">
        <!-- 仅 knowledge 走 App 级 keep-alive；勿用 route.meta.keepAlive（子路由 meta 会合并，曾导致角色页整页重建） -->
        <keep-alive v-if="route.name === 'knowledge'">
          <component :is="Component" :key="topLevelRouteKey(route)" />
        </keep-alive>
        <component v-else :is="Component" :key="topLevelRouteKey(route)" />
      </template>
    </router-view>
    <loading />
    <BrowserPageOverlay v-if="showBrowserPageOverlay" />
  </el-container>
</template>

<script setup lang="ts">
import { provide, ref, nextTick, inject, onMounted, onBeforeUnmount } from 'vue';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import Loading from '@/components/Loading.vue';
import BrowserPageOverlay from '@/components/common/BrowserPageOverlay.vue';
import { syncNativeLoginFromWindow } from '@/utils/nativeLoginSync';
import { isAimetaAndroidWebView, isAimetaBrowser } from '@/utils/platform';
import { runAppUpdateCheck } from '@/composables/useAppUpdate';
import { installBrowserPageOverlayBridge } from '@/utils/browserPageOverlay';
import {
  stripNativeDebugOverlay,
  scheduleStripNativeDebugOverlay,
} from '@/utils/stripNativeDebugOverlay';
import { useRouter } from 'vue-router';

const mergeCheckpointIntoAppConfig = inject<(() => Promise<void>) | undefined>(
  'mergeCheckpointIntoAppConfig',
  undefined
);
const onNativeLoginSync = () => {
  if (isAimetaAndroidWebView()) {
    syncNativeLoginFromWindow();
  }
};

const router = useRouter();
const showBrowserPageOverlay = isAimetaBrowser();
if (isAimetaAndroidWebView()) {
  router.afterEach(() => {
    scheduleStripNativeDebugOverlay();
  });
}

onMounted(() => {
  installBrowserPageOverlayBridge();
  mergeCheckpointIntoAppConfig?.();
  if (isAimetaAndroidWebView()) {
    syncNativeLoginFromWindow();
    stripNativeDebugOverlay();
    window.addEventListener('aimeta:native-login-sync', onNativeLoginSync);
    void runAppUpdateCheck(true);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('aimeta:native-login-sync', onNativeLoginSync);
});

function topLevelRouteKey(route: RouteLocationNormalizedLoaded) {
  // 必须固定为同一字符串：若回退到 route.path，/home 下子路由切换会改 key，整页重建 Home（像刷新）
  if (route.matched.some((r) => r.name === 'home')) {
    return 'home';
  }
  const top = route.matched[0];
  return (top?.name as string | undefined) ?? top?.path ?? route.path;
}

const isRouterAlive = ref(true);
const reload = () => {
  isRouterAlive.value = false;
  nextTick(() => {
    isRouterAlive.value = true;
  });
};

provide('reload', reload);
</script>

<style scoped></style>
<style>
/* WebView / 高 DPI 缩放下与宿主窗口等高，避免整页出现滚动条 */
.el-container {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
</style>
